import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth, authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";
import notification_services from "services/notification_services";
import { setUserProfile } from "store/userSlice";
import {
  networkTypeSelector,
  userProfileSelector,
} from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";
import {
  SIGNED_OUT_MESSAGE,
  attemptsLeftMessage,
  classifyReauthFailure,
  clearReauthAttempts,
  spendReauthAttempt,
} from "utils/reauth";
import {
  OTP_LENGTH,
  classifyOtpFailure,
  formatCountdown,
  sanitizeOtp,
  useResendCountdown,
} from "hooks/useEmailOtp";

const EMAIL_RE = /\S+@\S+\.\S+/;

export type NotificationEmailStep = 1 | 2 | 3;

export interface UseNotificationEmailReturn {
  step: NotificationEmailStep;
  title: string;
  subtitle: string;
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  submitLabel: string;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  showSuccess: boolean;
  successMsg: string;
  /** Non-blocking note under the form, e.g. that a resend invalidated the previous code. */
  noticeMsg: string;
  canResend: boolean;
  resendLabel: string;
  /** True once the last allowed wrong password was spent: closing now ends the session. */
  endsSession: boolean;
  handleResend: () => Promise<void>;
  /** Back to the address field. The only recovery when the code went to a mistyped inbox. */
  handleChangeEmail: () => void;
  handleSubmit: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
  /** Return the flow to step 1 and drop anything in flight. Call when the modal closes. */
  reset: () => void;
}

/**
 * Change where transaction receipts are sent: prove the password, confirm the address, store it.
 *
 * The password step is what stops an unattended session being pointed at somebody else's inbox;
 * the address is only stored once a code mailed to it comes back, so a typo cannot quietly send a
 * cardholder's transaction history somewhere they did not choose.
 */
export function useNotificationEmail(): UseNotificationEmailReturn {
  const dispatch = useDispatch();
  const { signOut } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  const profile = useSelector(userProfileSelector);

  const [step, setStep] = useState<NotificationEmailStep>(1);
  const [currentPassword, setCurrentPassword] = useState("");
  // Prefilled with whichever address is already on file — the one receipts go to now, or failing
  // that the sign-in address. Editable: the point of the screen is to be able to change it.
  const [email, setEmail] = useState(
    profile?.notificationEmail ?? profile?.email ?? "",
  );
  const [otp, setOtp] = useState("");

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [noticeMsg, setNoticeMsg] = useState("");

  /**
   * Set on the last allowed wrong password. The sign-out waits for the close rather than firing
   * there and then: AuthContext's auth listener runs `if (didInit && !user) replace("/")`, which
   * would take the page away before the message could be read.
   *
   * The ref is the authority and the state is only its render mirror: the unmount cleanup below
   * has to be able to consume the flag, and a closed-over state value cannot be consumed.
   */
  const signOutOnCloseRef = useRef(false);
  const [endsSession, setEndsSession] = useState(false);

  const {
    secondsLeft,
    start: startResendCountdown,
    stop: stopResendCountdown,
  } = useResendCountdown();

  /**
   * Set once the code has been accepted. If the save that follows fails for its own reason, a
   * retry must not re-submit the code — the server treats a re-submit as idempotent, but skipping
   * it keeps the retry honest about which leg actually failed.
   */
  const emailVerifiedRef = useRef(false);

  /**
   * Bumped when the screen is left. Every async action captures it and drops its own setState
   * calls if the value moved, so navigating away mid-request cannot write into an unmounted form.
   */
  const runIdRef = useRef(0);

  const passwordRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const isStale = (run: number) => runIdRef.current !== run;

  /**
   * Fires the deferred sign-out, once. Consuming through the ref is what makes it idempotent, so
   * the close path and the unmount cleanup can both call it.
   */
  const consumePendingSignOut = () => {
    if (!signOutOnCloseRef.current) return;
    signOutOnCloseRef.current = false;
    setEndsSession(false);
    // AuthContext's signOut, not authService's: it revokes the server session through signOutApi
    // and clears the react-query cache, the in-memory app token / session key and the idle clock.
    // The provider's own would leave the __session cookie valid server-side for up to an hour.
    void signOut();
  };

  // A route change out of /user unmounts UserNav and this modal with it, without any close handler
  // running. Without this the spent lockout is simply forgotten and the session survives.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => consumePendingSignOut(), []);

  const clearError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => clearError();

  /**
   * Bumps runIdRef, so anything already awaiting a response belongs to the previous run and drops
   * its setState calls — closing the modal mid-request must not write a later step into a form the
   * user already dismissed, which would reopen it there.
   */
  const reset = () => {
    runIdRef.current += 1;
    setStep(1);
    setCurrentPassword("");
    setEmail(profile?.notificationEmail ?? profile?.email ?? "");
    setOtp("");
    setNoticeMsg("");
    stopResendCountdown();
    emailVerifiedRef.current = false;
    // Overlays too: an error or loading modal left standing outlives the form that opened it and
    // covers the page with no way to dismiss it.
    clearError();
    setShowLoading(false);
    setLoadingMsg("");
    setShowSuccess(false);
    setSuccessMsg("");

    // Last, because it navigates: every close path runs reset, so a pending sign-out cannot be
    // escaped by dismissing the modal instead of the error.
    consumePendingSignOut();
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  /**
   * A code sent to a mistyped address never arrives, and no amount of resending fixes it. Going
   * back drops the verified flag: whatever was proved belongs to the OLD address, and carrying it
   * forward would let the next submit store an address nobody confirmed. It stops at the address
   * step — re-proving the password over an email typo would spend guesses on the wrong mistake.
   */
  const handleChangeEmail = () => {
    clearError();
    setOtp("");
    setNoticeMsg("");
    stopResendCountdown();
    emailVerifiedRef.current = false;
    setStep(2);
  };

  // ── Step 1 — prove the current password. Only a wrong one spends an attempt. ─
  const submitPassword = async () => {
    if (!currentPassword)
      return openError("Please enter your current password.");

    const run = runIdRef.current;
    try {
      setLoadingMsg("Checking your current password…");
      setShowLoading(true);
      await authService.reauthenticate(currentPassword);
      if (isStale(run)) return;
      clearReauthAttempts();
      // Belt and braces against a latched flag: handleSubmit already refuses to run once the
      // lockout is armed, so this is unreachable — but a stale arming would sign out a user who
      // just proved their password, which is the worst way for this to fail.
      signOutOnCloseRef.current = false;
      setEndsSession(false);
      setShowLoading(false);
      setLoadingMsg("");
      // Nothing downstream needs it, and the remaining two steps are network round trips.
      setCurrentPassword("");
      setStep(2);
    } catch (err: any) {
      const { message, isWrongPassword } = classifyReauthFailure(
        err,
        "We couldn't verify your current password. Please try again.",
      );
      // Spent before the staleness check: closing the modal on a wrong guess must not dodge it.
      const left = isWrongPassword ? spendReauthAttempt() : 0;
      if (isStale(run)) return;

      if (!isWrongPassword) {
        // Throttled, offline, or something else that is not a failed guess.
        return openError(message);
      }

      setCurrentPassword("");

      if (left <= 0) {
        // No focus() here on purpose: the field stays mounted behind the error overlay, and
        // CustomModalV1 traps neither focus nor keystrokes — parking the caret there would invite
        // the user to type straight past the lockout.
        signOutOnCloseRef.current = true;
        setEndsSession(true);
        return openError(SIGNED_OUT_MESSAGE);
      }
      passwordRef.current?.focus();
      openError(attemptsLeftMessage(message, left));
    }
  };

  // ── Step 2 — mail a code to the address the cardholder chose ────────────────
  const submitEmail = async () => {
    const address = email.trim();
    if (!EMAIL_RE.test(address))
      return openError("Please enter a valid email address.");

    const run = runIdRef.current;
    try {
      setLoadingMsg("Sending verification code…");
      setShowLoading(true);
      await notification_services.verifyEmail({ email: address }, networkType);
      if (isStale(run)) return;
      setShowLoading(false);
      setOtp("");
      // A fresh address invalidates any code already accepted for the previous one.
      emailVerifiedRef.current = false;
      setNoticeMsg(`We sent a ${OTP_LENGTH}-digit code to ${address}.`);
      startResendCountdown();
      setStep(3);
    } catch (err: any) {
      if (isStale(run)) return;
      openError(
        apiErrorMessage(
          err,
          "Failed to send the verification code. Please try again.",
        ),
      );
    }
  };

  // ── Resend — replaces the previous code, so say so ──────────────────────────
  const handleResend = async () => {
    const run = runIdRef.current;
    clearError();
    try {
      setLoadingMsg("Sending a new code…");
      setShowLoading(true);
      await notification_services.verifyEmail(
        { email: email.trim() },
        networkType,
      );
      if (isStale(run)) return;
      setShowLoading(false);
      setOtp("");
      // The server overwrites the record, so the earlier code stops working immediately. Saying
      // this is what stops someone typing a slow-arriving first email and burning a guess.
      setNoticeMsg(
        "A new code is on its way. Use the most recent email — the previous code no longer works.",
      );
      startResendCountdown();
      emailVerifiedRef.current = false;
    } catch (err: any) {
      if (isStale(run)) return;
      const { code, message } = classifyOtpFailure(
        err,
        "Failed to resend the code. Please try again.",
      );
      // The server's own 60s floor. Keep the countdown running rather than leaving the button
      // enabled while every press is refused.
      if (code === "RATE_LIMITED" && secondsLeft === 0) startResendCountdown();
      openError(message);
    }
  };

  // ── Step 3 — check the code, then store the address ─────────────────────────
  const submitCode = async () => {
    if (otp.trim().length !== OTP_LENGTH)
      return openError(`Please enter the ${OTP_LENGTH}-digit code.`);

    const run = runIdRef.current;
    const address = email.trim();

    // ── Leg 1: the code. Only here can a failure mean "wrong or dead OTP". ──
    if (!emailVerifiedRef.current) {
      try {
        setLoadingMsg("Verifying code…");
        setShowLoading(true);
        await notification_services.verifyOtp(
          { email: address, otp: otp.trim() },
          networkType,
        );
        if (isStale(run)) return;
        emailVerifiedRef.current = true;
      } catch (err: any) {
        if (isStale(run)) return;
        const { isWrongCode, needsNewCode, message } = classifyOtpFailure(
          err,
          "Failed to verify the code. Please try again.",
        );
        if (isWrongCode) {
          // Clear it: resubmitting the same wrong digits spends another of the five
          // server-side guesses.
          setOtp("");
          otpRef.current?.focus();
          return openError(message);
        }
        if (needsNewCode) {
          setOtp("");
          stopResendCountdown();
          setNoticeMsg("That code can no longer be used. Request a new one.");
          return openError(message);
        }
        return openError(message);
      }
    }

    // ── Leg 2: the save. A failure here is NOT an OTP problem — RATE_LIMITED on this leg is
    // not the guess cap, and reading it as one would discard a verification still valid.
    try {
      setLoadingMsg("Turning on email notifications…");
      setShowLoading(true);
      const { notificationEmail } =
        await notification_services.setNotificationEmail(
          { email: address },
          networkType,
        );
      if (isStale(run)) return;

      // The store is the only copy the UI reads and it is persisted across reloads, with nothing
      // that re-fetches the profile — so without this the drawer keeps offering to enable what is
      // already on. Unconditional, with defaults first: the profile is undefined for a session
      // that predates it being stored, and skipping the dispatch there would strand the drawer.
      // setUserProfile REPLACES, hence the spread.
      dispatch(
        setUserProfile({
          mustChangePassword: false,
          cardNumber: null,
          email: null,
          ...profile,
          notificationEmail,
        }),
      );

      setShowLoading(false);
      setNoticeMsg("");
      stopResendCountdown();
      setSuccessMsg(
        `Email notifications are on. Receipts will go to ${notificationEmail}.`,
      );
      setShowSuccess(true);
    } catch (err: any) {
      if (isStale(run)) return;
      const { code, message } = classifyOtpFailure(
        err,
        "Failed to turn on email notifications. Please try again.",
      );
      // EMAIL_NOT_VERIFIED is the ONLY code on this leg that means the verification is gone —
      // assertEmailVerified is its sole domain thrower. RATE_LIMITED here is platform throttling,
      // not the guess cap, and treating it as a dead code would throw away a valid verification
      // and send the user to fetch a code they never needed.
      if (code === "EMAIL_NOT_VERIFIED") {
        setOtp("");
        stopResendCountdown();
        emailVerifiedRef.current = false;
        setNoticeMsg("That code can no longer be used. Request a new one.");
        return openError(message);
      }
      openError(message);
    }
  };

  const handleSubmit = async () => {
    // The lockout is terminal. Without this the error overlay is the only thing stopping another
    // go, and it stops the mouse but not the keyboard — clearError() below would then wipe the
    // "you've been signed out" message and let the flow carry on as if nothing had happened.
    if (signOutOnCloseRef.current) return;
    // Any new attempt supersedes the last failure, so a successful retry cannot leave the error
    // modal standing over a screen that already worked.
    clearError();
    if (step === 1) await submitPassword();
    else if (step === 2) await submitEmail();
    else await submitCode();
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const passwordFields: AuthFormField[] = [
    {
      name: "reauthPassword",
      label: "Current Password",
      type: "password",
      value: currentPassword,
      onChange: setCurrentPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Enter your current password",
      autoComplete: "current-password",
      inputRef: passwordRef,
    },
  ];

  const emailFields: AuthFormField[] = [
    {
      name: "notificationEmail",
      label: "Email for receipts",
      type: "email",
      value: email,
      onChange: setEmail,
      onKeyDown: submitOnEnter,
      placeholder: "you@email.com",
      autoComplete: "email",
    },
  ];

  const otpFields: AuthFormField[] = [
    {
      name: "otp",
      label: "Verification Code",
      type: "text",
      value: otp,
      // Digits only, capped at OTP_LENGTH — the field cannot hold a value the server would
      // reject for shape, so a wasted guess can only come from a genuinely wrong code.
      onChange: (val) => setOtp(sanitizeOtp(val)),
      onKeyDown: submitOnEnter,
      placeholder: "12345",
      autoComplete: "one-time-code",
      inputRef: otpRef,
    },
  ];

  const isEnabled = Boolean(profile?.notificationEmail);

  // Keyed by step rather than chained ternaries: a Record over the step union is the only form the
  // compiler checks for completeness, so a fourth step can never silently inherit another's copy.
  const titles: Record<NotificationEmailStep, string> = {
    1: "Confirm your password",
    2: isEnabled ? "Change your receipts email" : "Enable email notifications",
    3: "Enter your code",
  };

  const subtitles: Record<NotificationEmailStep, string> = {
    1: isEnabled
      ? "For your security, confirm your current password before changing where receipts are sent."
      : "For your security, confirm your current password before turning on email receipts.",
    2: "We will email you a receipt after every payment and refund. Confirm the address first.",
    3: `Enter the ${OTP_LENGTH}-digit code we sent to ${email.trim()}.`,
  };

  const fieldsByStep: Record<NotificationEmailStep, AuthFormField[]> = {
    1: passwordFields,
    2: emailFields,
    3: otpFields,
  };

  const submitLabels: Record<NotificationEmailStep, string> = {
    1: "Continue",
    2: "Send Code",
    3: "Verify & Turn On",
  };

  const submitDisabled: Record<NotificationEmailStep, boolean> = {
    1: !currentPassword,
    2: !EMAIL_RE.test(email.trim()),
    3: otp.length !== OTP_LENGTH,
  };

  return {
    step,
    title: titles[step],
    subtitle: subtitles[step],
    fields: fieldsByStep[step],
    submitRef,
    submitLabel: submitLabels[step],
    // The armed lockout disables every step, not just the one it happened on.
    isSubmitDisabled: endsSession || submitDisabled[step],
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    noticeMsg,
    canResend: step === 3 && secondsLeft === 0,
    resendLabel:
      secondsLeft > 0
        ? `Request a new code in ${formatCountdown(secondsLeft)}`
        : "Send a new code",
    endsSession,
    handleResend,
    handleChangeEmail,
    handleSubmit,
    handleCloseError,
    handleCloseSuccess,
    reset,
  };
}
